import { TEAM_DIVISION, type DefaultRsvp, type SessionKind, type TeamDivision } from '@ukinfo/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';
import { TEAM_DIVISION_LABELS, type Team } from '../lib/sports';
import { invalidateSports } from '../lib/sports-termine';
import { Button, Card, CardBody, Checkbox, Field, Input, LoadingBlock, SectionTitle, Select, useToast } from './ui';

interface TeamSettings {
  canEdit: boolean;
  borrow: { younger: number; older: number };
  showPersonalStats: boolean;
  kinds: { kind: SessionKind; allowMaybe: boolean; defaultRsvp: DefaultRsvp; responseDeadlineHours: number }[];
  updatedSessions?: number;
}

const KIND_LABELS: Record<SessionKind, string> = {
  TRAINING: 'Trainings',
  MATCH: 'Spiele',
  WORK: 'Arbeitseinsätze',
};

/** Auswahl für die Rückmeldefrist, in Stunden vor Beginn. */
const FRISTEN: { stunden: number; label: string }[] = [
  { stunden: 0, label: 'bis Beginn' },
  { stunden: 3, label: '3 Stunden vorher' },
  { stunden: 6, label: '6 Stunden vorher' },
  { stunden: 12, label: '12 Stunden vorher' },
  { stunden: 24, label: '1 Tag vorher' },
  { stunden: 48, label: '2 Tage vorher' },
  { stunden: 72, label: '3 Tage vorher' },
  { stunden: 168, label: '1 Woche vorher' },
];

function fristText(stunden: number): string {
  return FRISTEN.find((frist) => frist.stunden === stunden)?.label ?? `${stunden} Stunden vorher`;
}

/**
 * Rückmeldeeinstellungen einer Mannschaft, getrennt nach Terminart.
 *
 * Neue Terminarten erscheinen hier automatisch, sobald der Server sie
 * liefert. Eine Änderung gilt für neue und alle kommenden Termine.
 */
export function TeamSettingsCard({ teamId, youthAgeClass }: { teamId: string; youthAgeClass?: number | null }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const queryKey = ['sports-team-settings', teamId];

  const settings = useQuery({
    queryKey,
    queryFn: () => api.get<TeamSettings>(`/api/sports/teams/${teamId}/settings`),
  });

  const speichern = useMutation({
    mutationFn: (input: { kind: SessionKind; allowMaybe?: boolean; defaultRsvp?: DefaultRsvp; responseDeadlineHours?: number }) =>
      api.put<TeamSettings>(`/api/sports/teams/${teamId}/settings`, { kinds: [input] }),
    onSuccess: async (result, input) => {
      qc.setQueryData(queryKey, result);
      await invalidateSports(qc);
      const kommend = result.updatedSessions ?? 0;
      const art = KIND_LABELS[input.kind] ?? input.kind;
      const text = input.responseDeadlineHours !== undefined
        ? `${art}: Rückmeldung ${fristText(input.responseDeadlineHours)}`
        : input.defaultRsvp !== undefined
          ? `${art}: ohne Antwort ${input.defaultRsvp === 'YES' ? 'zugesagt' : 'offen'}`
          : `„Vielleicht“ für ${art} ${input.allowMaybe ? 'erlaubt' : 'deaktiviert'}`;
      toast.show(text + (kommend > 0 ? ` – ${kommend} kommende Termine angepasst` : ''));
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Einstellung konnte nicht gespeichert werden', 'error'),
  });

  const teilnahmeZeigen = useMutation({
    mutationFn: (showPersonalStats: boolean) =>
      api.put<TeamSettings>(`/api/sports/teams/${teamId}/settings`, { showPersonalStats }),
    onSuccess: (result, wert) => {
      qc.setQueryData(queryKey, result);
      toast.show(wert ? 'Spieler sehen ihre eigene Teilnahme' : 'Eigene Teilnahme ausgeblendet');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Einstellung konnte nicht gespeichert werden', 'error'),
  });

  const jahrgaenge = useMutation({
    mutationFn: (borrow: { younger: number; older: number }) =>
      api.put<TeamSettings>(`/api/sports/teams/${teamId}/settings`, { borrow }),
    onSuccess: (result) => {
      qc.setQueryData(queryKey, result);
      toast.show('Vorschläge fürs Dazuholen gespeichert');
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Einstellung konnte nicht gespeichert werden', 'error'),
  });
  const borrow = settings.data?.borrow;

  return (
    <>
    {youthAgeClass && borrow && (
      <Card>
        <CardBody className="space-y-3">
          <SectionTitle title="Spieler dazuholen" subtitle={`Welche Jahrgänge für die U${youthAgeClass} vorgeschlagen werden`} />
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Jüngere Jahrgänge" hint="Spieler, die hochrücken">
              {(id) => (
                <Select id={id} value={borrow.younger} disabled={!settings.data?.canEdit || jahrgaenge.isPending}
                  onChange={(event) => jahrgaenge.mutate({ ...borrow, younger: Number(event.target.value) })}>
                  {[0, 1, 2, 3, 4].map((n) => <option key={n} value={n}>{n === 0 ? 'keine' : `bis ${n} jünger (bis U${youthAgeClass - n})`}</option>)}
                </Select>
              )}
            </Field>
            <Field label="Ältere Jahrgänge" hint="Nur mit gültiger Spielberechtigung">
              {(id) => (
                <Select id={id} value={borrow.older} disabled={!settings.data?.canEdit || jahrgaenge.isPending}
                  onChange={(event) => jahrgaenge.mutate({ ...borrow, older: Number(event.target.value) })}>
                  {[0, 1, 2].map((n) => <option key={n} value={n}>{n === 0 ? 'keine' : `bis ${n} älter (bis U${youthAgeClass + n})`}</option>)}
                </Select>
              )}
            </Field>
          </div>
          <p className="text-xs text-slate-500">
            Nur Vorschläge: Beim Dazuholen lassen sich mit „Alle Jahrgänge zeigen“ auch andere Spieler bewusst einplanen.
          </p>
        </CardBody>
      </Card>
    )}
    <Card>
      <CardBody className="space-y-4">
        <SectionTitle title="Rückmeldungen" subtitle="Was Spieler bei Zu- und Absagen wählen können" />
        {settings.isLoading && <LoadingBlock />}
        {settings.error && <p className="text-sm text-rose-700">Die Einstellungen konnten nicht geladen werden.</p>}
        {settings.data && (
          <>
            <div className="divide-y divide-slate-100 rounded-xl border border-slate-200">
              {settings.data.kinds.map((eintrag) => (
                <div key={eintrag.kind} className="flex flex-wrap items-center justify-between gap-3 px-3 py-3">
                  <div className="min-w-0 basis-48 flex-1">
                    <p className="text-sm font-medium text-slate-900">{KIND_LABELS[eintrag.kind] ?? eintrag.kind}</p>
                    <p className="text-xs text-slate-500">
                      {eintrag.allowMaybe ? 'Zusage, Vielleicht (mit Notiz) oder Absage' : 'Nur Zusage oder Absage'}
                      {eintrag.defaultRsvp === 'YES' && ' · alle eingeplant, nur Absagen nötig'}
                      {` · Frist ${fristText(eintrag.responseDeadlineHours)}`}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      Frist
                      <Select
                        aria-label={`Rückmeldefrist bei ${KIND_LABELS[eintrag.kind] ?? eintrag.kind}`}
                        className="h-8 w-auto rounded-lg py-0 pl-2.5 text-sm"
                        value={eintrag.responseDeadlineHours}
                        disabled={!settings.data.canEdit || speichern.isPending}
                        onChange={(event) => speichern.mutate({ kind: eintrag.kind, responseDeadlineHours: Number(event.target.value) })}
                      >
                        {FRISTEN.some((frist) => frist.stunden === eintrag.responseDeadlineHours) ? null : (
                          <option value={eintrag.responseDeadlineHours}>{fristText(eintrag.responseDeadlineHours)}</option>
                        )}
                        {FRISTEN.map((frist) => <option key={frist.stunden} value={frist.stunden}>{frist.label}</option>)}
                      </Select>
                    </label>
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      Ohne Antwort
                      <Select
                        aria-label={`Ohne Antwort bei ${KIND_LABELS[eintrag.kind] ?? eintrag.kind}`}
                        className="h-8 w-auto rounded-lg py-0 pl-2.5 text-sm"
                        value={eintrag.defaultRsvp}
                        disabled={!settings.data.canEdit || speichern.isPending}
                        onChange={(event) => speichern.mutate({ kind: eintrag.kind, defaultRsvp: event.target.value as DefaultRsvp })}
                      >
                        <option value="PENDING">offen</option>
                        <option value="YES">zugesagt</option>
                      </Select>
                    </label>
                    <Checkbox
                      label="„Vielleicht“ erlauben"
                      checked={eintrag.allowMaybe}
                      disabled={!settings.data.canEdit || speichern.isPending}
                      onChange={(wert) => speichern.mutate({ kind: eintrag.kind, allowMaybe: wert })}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-slate-200 px-3 py-3">
              <Checkbox
                label="Eigene Teilnahme im persönlichen Link zeigen"
                checked={settings.data.showPersonalStats}
                disabled={!settings.data.canEdit || teilnahmeZeigen.isPending}
                onChange={(wert) => teilnahmeZeigen.mutate(wert)}
              />
              <p className="mt-1 text-xs text-slate-500">
                Jeder Spieler sieht nur sich selbst: ein farbiges Band mit Wort, keine Prozentzahl und kein Vergleich
                mit der Mannschaft. Entschuldigtes Fehlen zählt nicht gegen ihn.
              </p>
            </div>
            <p className="text-xs text-slate-500">
              Gilt für neue und alle kommenden Termine der Mannschaft. Die Frist bestimmt, wann Spieler an eine
              fehlende Antwort erinnert werden und ab wann eine Änderung als verspätet gilt. Vergangene Termine und bereits gegebene
              Antworten bleiben unverändert. Bei „ohne Antwort zugesagt“ gilt eine eingetragene Abwesenheit weiterhin
              als Absage; Aushilfen bleiben offen und es gibt keine Erinnerungen zum Antworten.
              {!settings.data.canEdit && ' Ändern kann nur die Mannschaftsleitung.'}
            </p>
          </>
        )}
      </CardBody>
    </Card>
    </>
  );
}

/**
 * Name und Damen/Herren einer Mannschaft in der gewählten Saison.
 *
 * Der Name gilt je Saison: eine Umbenennung ändert frühere Saisonen nicht.
 * Damen/Herren wurde beim ersten Einspielen aus Name und Art abgeleitet -
 * hier lässt es sich korrigieren.
 */
export function TeamDivisionCard({ team, seasonId }: { team: Team; seasonId: string }): JSX.Element {
  const toast = useToast();
  const qc = useQueryClient();
  const [name, setName] = useState(team.name);
  useEffect(() => setName(team.name), [team.name]);

  const speichern = useMutation({
    mutationFn: (input: { name: string; division: TeamDivision }) =>
      api.patch(`/api/sports/teams/${team.id}`, {
        name: input.name, organisationUnitId: team.organisation_unit_id, seasonId,
        category: team.category ?? 'OTHER', ageClass: team.category === 'YOUTH' ? team.ageClass ?? null : null, division: input.division,
      }),
    onSuccess: async (_result, input) => {
      await invalidateSports(qc);
      toast.show(input.name !== team.name ? `Umbenannt in „${input.name}“` : `Als ${TEAM_DIVISION_LABELS[input.division]} gespeichert`);
    },
    onError: (error: unknown) =>
      toast.show(error instanceof ApiError ? error.message : 'Konnte nicht gespeichert werden', 'error'),
  });
  const division = team.division ?? 'MIXED';
  const neuerName = name.trim();

  return (
    <Card>
      <CardBody className="space-y-3">
        <SectionTitle title="Mannschaft" subtitle="Gilt für die gewählte Saison" />
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            if (neuerName && neuerName !== team.name) speichern.mutate({ name: neuerName, division });
          }}
        >
          <div className="min-w-0 flex-1 basis-60">
            <Field label="Name" hint="So erscheint die Mannschaft in Listen, Links und E-Mails">
              {(id) => <Input id={id} value={name} maxLength={160} onChange={(event) => setName(event.target.value)} />}
            </Field>
          </div>
          <Button type="submit" variant="secondary" className="mb-5" disabled={!neuerName || neuerName === team.name} loading={speichern.isPending}>
            Umbenennen
          </Button>
        </form>
        <Field label="Damen / Herren" hint="Für Filter und Übersichten in der Mannschaftsliste">
          {(id) => (
            <Select
              id={id}
              className="sm:w-60"
              value={division}
              disabled={speichern.isPending}
              onChange={(event) => speichern.mutate({ name: team.name, division: event.target.value as TeamDivision })}
            >
              {TEAM_DIVISION.map((value) => <option key={value} value={value}>{TEAM_DIVISION_LABELS[value]}</option>)}
            </Select>
          )}
        </Field>
      </CardBody>
    </Card>
  );
}
